import * as React from "react"
import { Link, router } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import type { SummaryPaging, WeekOption } from "@/types/travel_tracker"

/**
 * The week picker and the way back and forward, shared by both summaries so the
 * two pages cannot drift into offering different weeks or stepping by different
 * amounts.
 *
 * A Combobox rather than a Select: the list runs from January 2026 to a year past
 * today and reaches every week the imported history touches, which is already
 * past a hundred options.
 */
export function WeekStrip({
  week,
  weeks,
  paging,
  path,
}: {
  week: WeekOption
  weeks: WeekOption[]
  paging: SummaryPaging
  path: string
}) {
  const options = React.useMemo(
    () => weeks.map((option) => ({ value: option.value, label: option.label })),
    [weeks],
  )

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-xs">
        <label htmlFor="week">Week</label>
        <div className="mt-1">
          <Combobox
            id="week"
            options={options}
            value={week.value}
            onChange={(value) => router.get(path, { week: value }, { preserveState: false })}
            searchPlaceholder="Jump to a month..."
            triggerLabel="Choose a week"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="secondary" asChild>
          <Link href={`${path}?week=${paging.earlier}`}>
            <ChevronLeft className="size-4" aria-hidden="true" />
            Earlier
          </Link>
        </Button>
        {paging.current && (
          <Button variant="ghost" asChild>
            <Link href={`${path}?week=${paging.current}`}>This week</Link>
          </Button>
        )}
        <Button variant="secondary" asChild>
          <Link href={`${path}?week=${paging.later}`}>
            Later
            <ChevronRight className="size-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </div>
  )
}
